import React, { useReducer, useState, useEffect } from 'react';
import * as qs from 'qs';
import { useHistory } from 'react-router-dom';
import { checkAuthState, signIn } from '../../../utils/APIUtils';
import { USER } from '../../../constants/CommonConst';
import { notificationManager } from '../../../utils/NotificationUtils';
import { getUser } from '../../../utils/PropertyAPIUtils';
import TextField from '@material-ui/core/TextField';
import { InputAdornment } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import VisibilityIcon from '@material-ui/icons/Visibility';
import './login.scss';
import ArrowBack from '@material-ui/icons/ArrowBack';
import { Button, CircularProgress } from '@material-ui/core';
import { VisibilityOff } from '@material-ui/icons';
import { handleValidation } from '../../../utils/formUtils';
import { useDispatch } from 'react-redux';
import { setAllBrands, setAllGroups, setAllHotels } from '../../property/propertySlice';
import { encryptLocalStorage, decryptLocalStorage, handleLogout } from '../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../utils/loaderSlice';
import { confirmSignInWithMFA, getMFAStatus, setupMFA, verifyMFASetup, generateQRCodeURL } from '../../../utils/mfaUtils';
import QRCode from 'qrcode';

const Login = (props) => {
  const { onMfaStateChange } = props;
  const history = useHistory();
  const preloginEmail = sessionStorage.getItem('prelogin_email') || '';
  const [fields, setFields] = useReducer((state, newState) => ({ ...state, ...newState }), {
    email: {
      value: preloginEmail,
      fieldName: 'Email',
      errors: {},
      validations: ['required', 'validEmail'],
    },
    password: {
      value: '',
      fieldName: 'Password',
      errors: {},
      validations: ['required'],
    },
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [title, setTitle] = useState('Log in');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaUser, setMfaUser] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [userNeedsMfaSetup, setUserNeedsMfaSetup] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (onMfaStateChange) {
      onMfaStateChange(mfaRequired || (showMfaSetup && userNeedsMfaSetup), resetToLoginForm);
    }
  }, [mfaRequired, showMfaSetup, userNeedsMfaSetup, onMfaStateChange]);

  const resetToLoginForm = () => {
    setMfaRequired(false);
    setMfaUser(null);
    setMfaCode('');
    setShowMfaSetup(false);
    setUserNeedsMfaSetup(false);
    setVerificationCode('');
    setQrCodeUrl('');
    setSecretCode('');
    setFields({
      email: { ...fields.email, value: '' },
      password: { ...fields.password, value: '' },
    });
    setTitle('Log in');
  };

  useEffect(() => {
    if (qs.parse(props.location.search, { ignoreQueryPrefix: true }).message) {
      setTitle('Welcome');
      setShowPassword(true);
    }
  }, [props.location.search]);

  useEffect(() => {
    if (showMfaSetup && userNeedsMfaSetup) {
      const handleBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = '';
        return '';
      };

      const handlePopState = (event) => {
        window.history.pushState(null, '', window.location.href);
        notificationManager('info', 'Please complete MFA setup to continue.');
      };

      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
      window.addEventListener('beforeunload', handleBeforeUnload);

      const handleKeyDown = (event) => {
        if (
          (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) ||
          (event.key === 'Backspace' && event.target.tagName !== 'INPUT' && event.target.tagName !== 'TEXTAREA')
        ) {
          event.preventDefault();
          notificationManager('info', 'Please complete MFA setup to continue.');
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [showMfaSetup, userNeedsMfaSetup]);

  const handleInputChange = (e, type) => {
    const inputValue = type === 'email' ? e.target.value?.replace(/\s+/g, '') : e.target.value;
    const currField = { ...fields }[type];

    currField.value = inputValue;
    handleValidation(currField);

    setFields({
      [type]: currField,
    });
  };

  useEffect(() => {
    (async () => {
      const currUser = await checkAuthState();
      const loggedinUser = decryptLocalStorage(USER);
      if (loggedinUser && currUser && currUser.signInUserSession.isValid()) {
        if (loggedinUser.role !== 'SUPER_ADMIN' && loggedinUser.mfaEnforced === true) {
          const currentMfaStatus = await getMFAStatus();
          
          if (!currentMfaStatus.enabled) {
            setUserNeedsMfaSetup(true);
            setShowMfaSetup(true);
            return;
          }
        }
        
        history.push('/');
      }
    })();
  }, []);

  const validate = () => {
    let isValid = true;
    const updatedFields = {};
    for (let field in fields) {
      const currField = { ...fields }[field];
      if (handleValidation(currField)) isValid = false;
      updatedFields[field] = currField;
    }
    setFields(updatedFields);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      const email = fields.email.value?.replace(/\s+/g, '')?.toLowerCase();
      try {
        dispatch(stepUpLoaderCount());
        const val = await signIn(email, fields.password.value);
        if (val) {
          if (val.challengeName && val.challengeName === 'NEW_PASSWORD_REQUIRED') {
            history.push({
              pathname: '/set-password',
              user: val,
              action: 'newPassword',
            });
            notificationManager('success', 'OTP verified');
          } else if (val.challengeName && val.challengeName === 'SOFTWARE_TOKEN_MFA') {
            setMfaRequired(true);
            setMfaUser(val);
            setLoading(false);
          } else {
            await handleSuccessfulLogin(val);
          }
        }
      } catch (e) {
        setLoading(false);
        if (title !== 'Log in' && e.toString().includes('NotAuthorizedException')) {
          notificationManager('danger', 'Incorrect OTP');
        } else if (title !== 'Log in' && e.toString().includes('UserNotFoundException')) {
          notificationManager('danger', 'Incorrect Email');
        } else {
          notificationManager('danger', 'Login failed - ' + e.message);
        }
      } finally {
        dispatch(stepDownLoaderCount());
      }
    }
  };

  const handleSuccessfulLogin = async (cognitoUser) => {
    try {
      sessionStorage.removeItem('prelogin_email');
      const result = await getUser(cognitoUser.username);
      const userData = result.data.getUserById;
      encryptLocalStorage(USER, userData);

      if (userData.role === 'SUPER_ADMIN' || cognitoUser.attributes?.['custom:role'] === 'SUPER_ADMIN') {
        dispatch(setAllGroups([]));
        dispatch(setAllBrands([]));
        dispatch(setAllHotels([]));
        setLoading(false);
        history.push('/');
        return;
      }

      if (userData.mfaEnforced === true) {
        const currentMfaStatus = await getMFAStatus();
        
        if (!currentMfaStatus.enabled) {
          setUserNeedsMfaSetup(true);
          setShowMfaSetup(true);
          setLoading(false);
          return;
        }
      }

      dispatch(setAllGroups([]));
      dispatch(setAllBrands([]));
      dispatch(setAllHotels([]));
      setLoading(false);
      history.push('/');

    } catch (error) {
      console.error('Error in handleSuccessfulLogin:', error);
      setLoading(false);
      notificationManager('danger', 'Login verification failed - ' + error.message);
    }
  };

  const handleMFASubmit = async (e) => {
    e.preventDefault();
    if (!mfaCode || mfaCode.length !== 6) {
      notificationManager('danger', 'Please enter a valid 6-digit MFA code');
      return;
    }

    setLoading(true);
    try {
      dispatch(stepUpLoaderCount());
      const val = await confirmSignInWithMFA(mfaUser, mfaCode);

      if (val) {
        await handleSuccessfulLogin(val);
        notificationManager('success', 'Login successful');
      }
    } catch (e) {
      setLoading(false);
      if (e.code === 'CodeMismatchException' || e.message.includes('Invalid code')) {
        notificationManager('danger', 'Invalid MFA code. Please try again.');
      } else {
        notificationManager('danger', 'MFA verification failed - ' + e.message);
      }
    } finally {
      dispatch(stepDownLoaderCount());
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaRequired(false);
    setMfaUser(null);
    setMfaCode('');
    setFields({
      email: { ...fields.email, value: '' },
      password: { ...fields.password, value: '' },
    });
  };

  const handleSetupMFA = async () => {
    setSetupLoading(true);
    try {
      const user = decryptLocalStorage(USER);
      const secret = await setupMFA();
      setSecretCode(secret);

      const otpAuthUrl = generateQRCodeURL(secret, user.email);
      const qrDataUrl = await QRCode.toDataURL(otpAuthUrl);
      setQrCodeUrl(qrDataUrl);
    } catch (error) {
      notificationManager('danger', 'Failed to setup MFA: ' + error.message);
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerifyMFASetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      notificationManager('danger', 'Please enter a valid 6-digit code');
      return;
    }

    setSetupLoading(true);
    try {
      const success = await verifyMFASetup(verificationCode);
      if (success) {
        setShowMfaSetup(false);
        setUserNeedsMfaSetup(false);
        setVerificationCode('');
        setQrCodeUrl('');
        setSecretCode('');
        notificationManager('success', 'MFA setup completed successfully! Redirecting to dashboard...');
        
        dispatch(setAllGroups([]));
        dispatch(setAllBrands([]));
        dispatch(setAllHotels([]));
        history.push('/');
      }
    } catch (error) {
      notificationManager('danger', 'Error verifying MFA setup');
    } finally {
      setSetupLoading(false);
    }
  };

  const nextPath = (path) => {
    history.push(path);
  };

  const change = () => {
    setTitle('Log in');
    history.push('/login');
  };

  const onClickBack = () => {
    setTitle('Log in');
    history.push('/prelogin');
  };

  if (mfaRequired) {
    return (
      <form onSubmit={handleMFASubmit} noValidate autoComplete='off'>
        <div className='row d-flex justify-content-center align-items-center mb-3 content mx-0'>
          <div className='header-row d-flex justify-content-center'>
            <span className='welcome-title'>Multi-Factor Authentication</span>
          </div>
        </div>
        <div className='row d-flex justify-content-center align-items-center mb-3 mx-0'>
          <div className='col-md-8 text-center'>
            <p style={{ color: '#666', fontSize: '14px' }}>
              Enter the 6-digit code from your authenticator app
            </p>
          </div>
        </div>
        <div className='row d-flex justify-content-center align-items-center mx-0'>
          <div className='col-md-5 mb-3 d-flex justify-content-center'>
            <TextField
              id='mfaCode'
              label='MFA Code'
              required
              className='col-md-6'
              value={mfaCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setMfaCode(value);
              }}
              inputProps={{
                maxLength: 6,
                pattern: '[0-9]*',
                inputMode: 'numeric',
                autoComplete: 'off',
              }}
              helperText='Enter 6-digit code'
            />
          </div>
        </div>
        <div className='row d-flex justify-content-center align-items-center mt-4 mx-0'>
          <div className='col-md-12 mb-3 d-flex justify-content-center'>
            {loading ? (
              <CircularProgress color='primary' />
            ) : (
              <Button variant='contained' type='submit' color='primary' size='large'>
                VERIFY
              </Button>
            )}
          </div>
          <div className='col-md-12 mb-3 d-flex justify-content-center'>
            <span className='forgot-password' onClick={handleBackToLogin}>
              Back to Login
            </span>
          </div>
        </div>
      </form>
    );
  }

  if (showMfaSetup && userNeedsMfaSetup) {
    return (
      <div className='mfa-setup-scrollable-container scroll-styled'>
        <div className='mfa-setup-content'>
          <div className='row d-flex justify-content-center align-items-center mb-3 content mx-0'>
            <div className='header-row d-flex justify-content-center'>
              <span className='welcome-title'>Setup Multi-Factor Authentication</span>
            </div>
          </div>
          <div className='row d-flex justify-content-center align-items-center mb-3 mx-0'>
            <div className='col-md-10 text-center'>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Your organization requires multi-factor authentication. Please set it up to continue.
              </p>
            </div>
          </div>

          {!qrCodeUrl && !secretCode ? (
            <div className='row d-flex justify-content-center align-items-center mt-4 mx-0'>
              <div className='col-md-12 mb-3 d-flex justify-content-center'>
                {setupLoading ? (
                  <CircularProgress color='primary' />
                ) : (
                  <Button variant='contained' color='primary' size='large' onClick={handleSetupMFA}>
                    SETUP MFA
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className='row d-flex justify-content-center align-items-center mb-2 mx-0'>
                <div className='col-md-8 text-center'>
                  <p style={{ color: '#666', fontSize: '12px', marginBottom: '10px' }}>
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  {qrCodeUrl && (
                    <div style={{ margin: '10px 0' }}>
                      <img src={qrCodeUrl} alt='MFA QR Code' style={{ maxWidth: '200px' }} />
                    </div>
                  )}
                  {secretCode && (
                    <div style={{ marginTop: '8px' }}>
                      <p style={{ color: '#666', fontSize: '10px', marginBottom: '4px' }}>Or enter this code manually:</p>
                      <div style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '6px', 
                        borderRadius: '4px', 
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        wordBreak: 'break-all',
                        margin: '0 0 8px 0'
                      }}>
                        {secretCode}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className='row d-flex justify-content-center align-items-center mx-0'>
                <div className='col-md-5 mb-2 d-flex justify-content-center'>
                  <TextField
                    id='verificationCode'
                    label='Verification Code'
                    required
                    className='col-md-6'
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(value);
                    }}
                    inputProps={{
                      maxLength: 6,
                      pattern: '[0-9]*',
                      inputMode: 'numeric',
                      autoComplete: 'off',
                    }}
                    helperText='Enter the 6-digit code from your authenticator app'
                  />
                </div>
              </div>

              <div className='row d-flex justify-content-center align-items-center mt-2 mx-0'>
                <div className='col-md-12 mb-3 d-flex justify-content-center'>
                  {setupLoading ? (
                    <CircularProgress color='primary' />
                  ) : (
                    <Button 
                      variant='contained' 
                      color='primary' 
                      size='large' 
                      onClick={handleVerifyMFASetup}
                      disabled={!verificationCode || verificationCode.length !== 6}
                    >
                      VERIFY & COMPLETE SETUP
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit} noValidate autoComplete='off'>
        <div className='row d-flex justify-content-center align-items-center mb-3 content mx-0'>
          <div className='header-row d-flex justify-content-center'>
            <span className='welcome-title'>{title}</span>
            <span>&nbsp;to</span>
            <span className='elevate ms-1'> &nbsp;elevate</span>
          </div>
        </div>
        <div className='row d-flex justify-content-center align-items-center  mx-0'>
          <div className='col-md-5 mb-3 d-flex justify-content-center'>
            <TextField
              id='email'
              label='Email'
              required
              className='col-md-6'
              value={fields.email.value}
            onChange={(e) => handleInputChange(e, 'email')}
              onKeyDown={(e) => {
                if (e?.key === ' ') {
                  e?.preventDefault();
                }
              }}
              error={fields.email.errors.required || fields.email.errors.validEmail}
              helperText={fields.email.errors.required || fields.email.errors.validEmail}
              inputProps={{
                autoComplete: 'off',
                readOnly: true,
                onFocus: (e) => e.target.removeAttribute('readonly'),
              }}
              InputProps={
                title === 'Log in' && {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }
              }
            />
          </div>
        </div>
        <div className='row d-flex justify-content-center align-items-center mt-4  mx-0'>
          <div className='col-md-5 mb-3 d-flex justify-content-center'>
            <TextField
              id='password'
              label={title === 'Log in' ? 'Password' : 'OTP'}
              required
              type='text'
              onChange={(e) => handleInputChange(e, 'password')}
              className='col-md-6'
              error={fields.password.errors.required}
              helperText={fields.password.errors.required}
              inputProps={{
                autoComplete: 'off',
                style: showPassword ? {} : { WebkitTextSecurity: 'disc' },
              }}
              InputProps={
                title === 'Log in' && {
                  endAdornment: (
                    <InputAdornment position='end'>
                      {!showPassword ? (
                        <VisibilityOff
                          className='visible-icon'
                          onClick={() => setShowPassword(true)}
                        />
                      ) : (
                        <VisibilityIcon
                          className='visible-icon'
                          onClick={() => setShowPassword(false)}
                        />
                      )}
                    </InputAdornment>
                  ),
                }
              }
            />
          </div>
        </div>
        <div className='row d-flex justify-content-center align-items-center mt-4 mx-0'>
          <div className='col-md-12 mb-3 d-flex justify-content-center'>
            {loading ? (
              <CircularProgress color='primary' />
            ) : (
              <Button variant='contained' type='submit' color='primary' size='large'>
                {title === 'Log in' ? 'LOGIN' : 'VERIFY'}
              </Button>
            )}
          </div>
          {title === 'Log in' && (
            <>
              <div className='col-md-12 mb-3 d-flex justify-content-center'>
                <span className='forgot-password' onClick={() => nextPath('/forgot-password')}>
                  Forgot password?
                </span>
              </div>
              <div className='col-md-12 mb-3 d-flex justify-content-center'>
                <div>
                  <Button
                    variant='text'
                    color='secondary'
                    size='small'
                    startIcon={<ArrowBack />}
                    onClick={(e) => onClickBack(e)}
                    className='me-2'
                  >
                    Back
                  </Button>
                </div>
              </div>
            </>
          )}

          {title !== 'Log in' && (
            <div className='col-md-12 mb-3 d-flex justify-content-center'>
              Already have an account? &nbsp;
              <span className='forgot-password' onClick={change}>
                {' '}
                Login
              </span>
            </div>
          )}
        </div>
      </form>
    </>
  );
};

export default Login;
